import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { App } from "./App";
import { UIProvider } from "./ui";
import { CataloguePrompt } from "./components/CataloguePrompt";
import { WineryNotice } from "./components/WineryNotice";
import { startCatalogue } from "./lib/catalogue";
import { pruneSubmittedBarcodes } from "./lib/contribute/barcodePrompt";
import { startNotices } from "./lib/contribute/notices";
import { startOutbox } from "./lib/contribute/outbox";
import { pruneEntries } from "./lib/dataCache";
import { getLocale, loadLocale } from "./lib/i18n";
import { initAnalytics } from "./lib/learn/analytics";
import { linkPushToDevice } from "./lib/push";
import { startInstall } from "./lib/pwaInstall";
import { routeOAuthReturn } from "./lib/social/auth";
import { captureInviteFromLocation } from "./lib/social/inviteLink";
import { startChunkRecovery } from "./lib/chunkRecovery";
import { startUpdates } from "./lib/updates";
// Global styles: the fonts, and ui/global.css (canvas, resets, keyframes). Everything else is styled
// by the design system's components (src/ui), whose rules Tamagui compiles with the code.
import "./styles/fonts.css";
import "./ui/global.css";

// Before the first render: the browser fires beforeinstallprompt once and early.
startInstall();
startChunkRecovery();
// Also before it: an OAuth return opens SignIn, and an invite token leaves the address bar before
// any screen, history entry or analytics event can hold it.
routeOAuthReturn();
captureInviteFromLocation();

const root = document.getElementById("root");
if (root === null) throw new Error("#root element missing from index.html");

// A non-Dutch reader's dictionary is one small chunk, awaited so the first frame is already in
// their language; if it cannot load (offline, not cached yet) the app renders in Dutch instead.
const render = (): void => {
  createRoot(root).render(
    <StrictMode>
      <UIProvider>
        <App />
        <CataloguePrompt />
        <WineryNotice />
      </UIProvider>
    </StrictMode>,
  );
};
loadLocale(getLocale()).then(render, render);

// Analytics and the feature flag load after the first route has painted: nothing
// the learner sees waits for them, and without a key (a build with no analytics
// project) they never load at all and the Academy stays off.
const startAnalytics = (): void => {
  initAnalytics();
};
if (typeof window.requestIdleCallback === "function") window.requestIdleCallback(startAnalytics);
else window.setTimeout(startAnalytics, 1000);

// What the edge offers as an offline catalogue is learned once the shell is up: it decides whether
// the one-time download question or an update nudge appears (components/CataloguePrompt.tsx).
if (typeof window.requestIdleCallback === "function") window.requestIdleCallback(startCatalogue);
else window.setTimeout(startCatalogue, 1500);

// Producers, wines and barcodes the user contributed offline are sent once the shell is up, and
// again whenever the device comes back online.
const startContributions = (): void => {
  startOutbox();
  void pruneSubmittedBarcodes();
  // What became of them is asked at start and whenever the app is focused again; a push
  // subscription granted earlier is linked to this device so the answer can reach it.
  startNotices();
  void linkPushToDevice(getLocale());
};
if (typeof window.requestIdleCallback === "function")
  window.requestIdleCallback(startContributions);
else window.setTimeout(startContributions, 2500);

// Cache entries nobody has asked for in months are swept once the shell is
// up: cheap, but never worth a millisecond of the first paint.
const sweep = (): void => {
  pruneEntries().catch(() => undefined);
};
if (typeof window.requestIdleCallback === "function") window.requestIdleCallback(sweep);
else window.setTimeout(sweep, 2000);

// The worker (src/sw.ts) registers at the document's own base path, so it works served at / or
// under /proef/ (druif), and keeps asking for a newer one while the app is open.
startUpdates();
